import React, { useRef } from 'react';
import RichTextEditor from '../../../../../../../ui/RichTextEditor';
import defaultWellnessEmailTemplate from '../../../Config/Containers/InivitationTemplate/email_template.json'; // adjust path if you place it elsewhere

interface InvitationTabProps {
  emailTemplate: string;
  onChange: (updated: string) => void;
  onSave: () => void;
  isSaving: boolean;
}

const MERGE_TAGS: { name: string; variable: string }[] = [
  { name: 'Applicant Name', variable: 'applicant_name' },
  { name: 'Company Name', variable: 'company_name' },
  { name: 'Job Title', variable: 'job_title_name' },
  { name: 'End of Assessment', variable: 'end_of_assessment' },
  { name: 'Support Email', variable: 'support_email' },
  { name: 'Wellness Screening Link', variable: 'wellness_url' },
];

// Default starting content for a job opening that's never had a template configured.
const DEFAULT_JSON_TEMPLATE = JSON.stringify(defaultWellnessEmailTemplate);

export const InvitationTab: React.FC<InvitationTabProps> = ({
                                                              emailTemplate,
                                                              onChange,
                                                              onSave,
                                                              isSaving,
                                                            }) => {
  const editorRef = useRef<any>(null);

  const handleEditorApply = (html: string) => {
    onChange(html);
  };

  const handleEditorReady = (editor: any) => {
    editorRef.current = editor;
  };

  const handleTagClick = (variable: string) => {
    editorRef.current
        ?.chain()
        .focus()
        .insertContent(`{{${variable}}}`)
        .run();
  };

  const handleSave = () => {
    const latestBody = editorRef.current
        ? editorRef.current.getHTML()
        : emailTemplate;
    onChange(latestBody);
    onSave();
  };

  const handleCancel = () => {
    if (editorRef.current && emailTemplate) {
      editorRef.current.commands.setContent(emailTemplate);
    }
  };

  const isValid = emailTemplate.trim() !== '';

  return (
      <div className='space-y-6'>

        { /* Heading */ }
        <h2 className='text-base font-extrabold text-slate-900'>
          Wellness Invitation Template
        </h2>

        { /* Body — RichTextEditor renders its own sidebar for variable insertion */ }
        <div>
          <label className='mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-slate-500'>
            Body
          </label>
          <RichTextEditor
              key='wellness-invitation'
              jsonStringTemplate={DEFAULT_JSON_TEMPLATE}
              value={emailTemplate || undefined}
              validVariables={MERGE_TAGS}
              saveTemplate={handleEditorApply}
              onEditorReady={handleEditorReady}
              buttonText='Apply Body'
          />
        </div>

        { /* Merge-tag chips below the editor (quick-insert shortcut) */ }
        <div>
          <p className='mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500'>
            Quick-insert merge tags
          </p>
          <div className='flex flex-wrap gap-2'>
            { MERGE_TAGS.map((tag) => (
                <button
                    key={tag.variable}
                    type='button'
                    onClick={() => handleTagClick(tag.variable)}
                    className='rounded-full border border-slate-200 bg-slate-50 px-3 py-1 font-mono text-xs font-medium text-slate-700 transition-colors hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700'
                >
                  { `{{${tag.variable}}}` }
                </button>
            )) }
          </div>
          <p className='mt-1.5 text-[11px] text-slate-400'>
            Clicks insert the tag at the current cursor position in the editor
          </p>
        </div>

        { /* Action row */ }
        <div className='flex justify-end gap-3 border-t border-slate-100 pt-5'>
          <button
              type='button'
              onClick={handleCancel}
              disabled={isSaving}
              className='rounded-lg border border-slate-300 bg-white px-5 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50'
          >
            Cancel
          </button>
          <button
              type='button'
              onClick={handleSave}
              disabled={!isValid || isSaving}
              className='inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50'
          >
            { isSaving ? (
                <>
                  <svg className='h-4 w-4 animate-spin' fill='none' viewBox='0 0 24 24'>
                    <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                    <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z' />
                  </svg>
                  Saving…
                </>
            ) : (
                'Save'
            ) }
          </button>
        </div>
      </div>
  );
};